import React, { useState, useEffect, useRef } from 'react';
import AdminLayout from '@/components/admin/AdminLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Car, Plus, Edit, Trash2, Search, User, Wrench, FileText, ShoppingCart, ChevronLeft, ChevronRight, Eye, Camera, Loader2, Handshake, Download, CheckCircle2, ClipboardList, Truck, Receipt, GripVertical, Star, Check } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '@/utils/api';
import axios from 'axios';
import SaleProcessDialog from '@/components/SaleProcessDialog';
import PreparationChecklist from '@/components/PreparationChecklist';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, rectSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTranslation } from 'react-i18next';

const EXPENSE_CATEGORIES = [
  { value: 'pieces', label: 'Pieces' },
  { value: 'main_oeuvre', label: 'Main d\'oeuvre' },
  { value: 'carrosserie', label: 'Carrosserie' },
  { value: 'peinture', label: 'Peinture' },
  { value: 'nettoyage', label: 'Nettoyage / Preparation' },
  { value: 'controle', label: 'Controle technique' },
  { value: 'autre', label: 'Autre' },
];

const CostSection = ({ vehicle, onUpdate }) => {
  const [expenses, setExpenses] = React.useState(vehicle.expenses || []);
  const [showAdd, setShowAdd] = React.useState(false);
  const [newExp, setNewExp] = React.useState({ label: '', amount: '', category: 'pieces' });
  const [loading, setLoading] = React.useState(false);

  const purchasePrice = vehicle.purchase_price || 0;
  const totalExpenses = expenses.reduce((s, e) => s + (e.amount || 0), 0);
  const coutRevient = purchasePrice + totalExpenses;
  const margin = (vehicle.selling_price || 0) - coutRevient;

  const addExpense = async () => {
    if (!newExp.label || !newExp.amount) { toast.error('Remplissez le libellé et le montant'); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/used-vehicles/${vehicle.id}/expenses`, {
        label: newExp.label, amount: parseFloat(newExp.amount), category: newExp.category,
      });
      const updatedExpenses = [...expenses, res.data.expense];
      setExpenses(updatedExpenses);
      setNewExp({ label: '', amount: '', category: 'pieces' });
      setShowAdd(false);
      toast.success('Frais ajoute');
      if (onUpdate) onUpdate({ ...vehicle, expenses: updatedExpenses });
    } catch { toast.error('Erreur'); }
    finally { setLoading(false); }
  };

  const deleteExpense = async (expId) => {
    try {
      await axios.delete(`${API}/used-vehicles/${vehicle.id}/expenses/${expId}`);
      const updatedExpenses = expenses.filter(e => e.id !== expId);
      setExpenses(updatedExpenses);
      toast.success('Frais supprime');
      if (onUpdate) onUpdate({ ...vehicle, expenses: updatedExpenses });
    } catch { toast.error('Erreur'); }
  };

  const fmt = (v) => `CHF ${(v || 0).toLocaleString('fr-CH', { minimumFractionDigits: 2 })}`;

  return (
    <div className="border-t pt-3" data-testid="cost-section">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Receipt className="w-4 h-4 text-slate-500" />
          <span className="text-sm font-semibold text-slate-700">Cout de revient</span>
        </div>
        <span className="text-lg font-bold text-slate-900" data-testid="cout-revient-total">{fmt(coutRevient)}</span>
      </div>
      <div className="grid grid-cols-3 gap-2 text-xs mb-2">
        <div className="bg-slate-50 rounded-lg p-2 text-center">
          <p className="text-slate-400">Achat</p>
          <p className="font-bold text-slate-700">{fmt(purchasePrice)}</p>
        </div>
        <div className="bg-slate-50 rounded-lg p-2 text-center">
          <p className="text-slate-400">Frais</p>
          <p className="font-bold text-orange-600">{fmt(totalExpenses)}</p>
        </div>
        <div className={`rounded-lg p-2 text-center ${margin >= 0 ? 'bg-emerald-50' : 'bg-red-50'}`}>
          <p className="text-slate-400">Marge</p>
          <p className={`font-bold ${margin >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{fmt(margin)}</p>
        </div>
      </div>
      {expenses.length > 0 && (
        <div className="space-y-1 mb-2 max-h-32 overflow-y-auto">
          {expenses.map(exp => (
            <div key={exp.id} className="flex items-center justify-between text-xs bg-slate-50 rounded-lg px-2 py-1.5 group">
              <div className="flex-1">
                <span className="font-medium text-slate-700">{exp.label}</span>
                <span className="text-slate-400 ml-2">{EXPENSE_CATEGORIES.find(c => c.value === exp.category)?.label || exp.category}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-semibold text-slate-700">{fmt(exp.amount)}</span>
                <button onClick={() => deleteExpense(exp.id)} className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600 transition-opacity" data-testid={`delete-expense-${exp.id}`}>
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      {!showAdd ? (
        <Button size="sm" variant="outline" className="w-full text-xs" onClick={() => setShowAdd(true)} data-testid="add-expense-btn">
          <Plus className="w-3 h-3 mr-1" /> Ajouter un frais
        </Button>
      ) : (
        <div className="space-y-2 bg-slate-50 rounded-lg p-2">
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="Libelle (ex: Freins)" value={newExp.label} onChange={e => setNewExp({...newExp, label: e.target.value})} className="text-xs h-8" data-testid="expense-label-input" />
            <Input type="number" placeholder="Montant CHF" value={newExp.amount} onChange={e => setNewExp({...newExp, amount: e.target.value})} className="text-xs h-8" data-testid="expense-amount-input" />
          </div>
          <Select value={newExp.category} onValueChange={v => setNewExp({...newExp, category: v})}>
            <SelectTrigger className="text-xs h-8" data-testid="expense-category-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {EXPENSE_CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="flex gap-2">
            <Button size="sm" className="flex-1 text-xs bg-blue-600 text-white" onClick={addExpense} disabled={loading} data-testid="save-expense-btn">
              {loading ? 'En cours...' : 'Ajouter'}
            </Button>
            <Button size="sm" variant="ghost" className="text-xs" onClick={() => setShowAdd(false)}>Annuler</Button>
          </div>
        </div>
      )}
    </div>
  );
};

const STATUS_MAP = {
  disponible: { label: 'Disponible', color: 'bg-emerald-100 text-emerald-700' },
  pret_a_livrer: { label: 'Prêt à livrer', color: 'bg-blue-100 text-blue-700' },
  reserve: { label: 'Réservé', color: 'bg-amber-100 text-amber-700' },
  pending_signature: { label: 'En attente', color: 'bg-red-100 text-red-700' },
  vendu: { label: 'Vendu', color: 'bg-slate-200 text-slate-600' },
};
const CONDITION_MAP = { excellent: 'Excellent', bon: 'Bon', moyen: 'Moyen', a_reviser: 'À réviser' };
const BODY_TYPES = ['Limousine', 'Break', 'Cabriolet', 'Coupé', 'SUV', 'Monospace', 'Fourgon', 'Pick-up', 'Autre'];
const VEHICLE_GENRES = ['Voiture de tourisme', 'Véhicule utilitaire', 'Motocycle', 'Remorque', 'Autre'];
const CANTONS = ['AG','AI','AR','BE','BL','BS','FR','GE','GL','GR','JU','LU','NE','NW','OW','SG','SH','SO','SZ','TG','TI','UR','VD','VS','ZG','ZH'];

const EMPTY_FORM = {
  category: 'nos_vehicules', deposant_name: '', deposant_phone: '',
  commission_percent: 0, commission_fixed: 0, deposit_date: '', contract_end_date: '',
  owner_name: '', owner_address: '', owner_street: '', owner_postal_code: '', owner_city: '',
  owner_dob: '', owner_origin: '',
  insurance_name: '', insurance_number: '', canton_notes: '',
  plate_number: '', vehicle_genre: '', brand: '', model: '',
  chassis_number: '', body_type: '', color: '', color_code: '',
  special_usage: '', seats_total: 5, seats_front: 2, serial_number: '',
  type_approval: '', displacement_cc: 0, power_kw: 0, empty_weight_kg: 0,
  total_weight_kg: 0, payload_kg: 0, towing_weight_kg: 0, co2_emissions: 0,
  emission_code: '', vehicle_code: '', first_registration: '',
  expertise_date: '', expertise_canton: '', mileage: 0,
  purchase_price: 0, selling_price: 0, status: 'disponible',
  condition: '', description: '',
  body_state: '', body_cost_indicative: 0, body_details: '',
  mech_state: '', mech_cost_indicative: 0, mech_details: '',
};

const TABS = [
  { id: 'vehicule', label: 'Véhicule', icon: Car },
  { id: 'technique', label: 'Technique', icon: Wrench },
  { id: 'detenteur', label: 'Détenteur', icon: User },
  { id: 'admin', label: 'Administration', icon: FileText },
  { id: 'vente', label: 'Vente', icon: ShoppingCart },
];

const UsedVehiclesPage = ({ auth, onLogout }) => {
  const { t } = useTranslation();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [activeTab, setActiveTab] = useState('vehicule');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showDetail, setShowDetail] = useState(null);
  const [saleVehicle, setSaleVehicle] = useState(null);
  const [saleContract, setSaleContract] = useState(null); // existing contract for editing
  const [soldVehicles, setSoldVehicles] = useState([]);
  const [soldContracts, setSoldContracts] = useState([]);
  const [pendingVehicles, setPendingVehicles] = useState([]);
  const [pendingContracts, setPendingContracts] = useState([]);
  const [viewTab, setViewTab] = useState('stock'); // stock | pret_a_livrer | pending | vendu
  const [scanning, setScanning] = useState(false);
  const [scanSession, setScanSession] = useState(null);
  const [prepDialog, setPrepDialog] = useState({ open: false, vehicleId: null, vehicleName: '', type: 'parc' });
  const [prepStatuses, setPrepStatuses] = useState({});
  const [publishVehicle, setPublishVehicle] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => { load(); loadSold(); loadPending(); }, []);

  const loadPrepStatuses = async (vehicleList) => {
    const statuses = {};
    for (const v of vehicleList) {
      try {
        const res = await axios.get(`${API}/used-vehicles/${v.id}/preparations`);
        statuses[v.id] = res.data;
      } catch { /* ignore */ }
    }
    setPrepStatuses(prev => ({ ...prev, ...statuses }));
  };

  const load = async () => {
    try {
      const res = await axios.get(`${API}/used-vehicles`);
      const stockVehicles = res.data.filter(v => v.status !== 'vendu' && v.status !== 'pending_signature');
      setVehicles(stockVehicles);
      loadPrepStatuses(stockVehicles);
    } catch { toast.error('Erreur de chargement'); }
    finally { setLoading(false); }
  };

  const loadPending = async () => {
    try {
      const cRes = await axios.get(`${API}/sale-contracts?status=draft`);
      setPendingContracts(cRes.data);
      const vehicleIds = cRes.data.map(c => c.vehicle_id);
      if (vehicleIds.length > 0) {
        const vRes = await axios.get(`${API}/used-vehicles?include_inactive=true`);
        const pv = vRes.data.filter(v => v.status === 'pending_signature');
        setPendingVehicles(pv);
        loadPrepStatuses(pv);
      } else {
        setPendingVehicles([]);
      }
    } catch {}
  };

  const loadSold = async () => {
    try {
      const cRes = await axios.get(`${API}/sale-contracts?status=concluded`);
      setSoldContracts(cRes.data);
      // Get sold vehicles from contracts
      const vehicleIds = cRes.data.map(c => c.vehicle_id);
      if (vehicleIds.length > 0) {
        const vRes = await axios.get(`${API}/used-vehicles?include_inactive=true`);
        setSoldVehicles(vRes.data.filter(v => v.status === 'vendu'));
      }
    } catch {}
  };

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // QR code scan session workflow
  const startScanSession = async () => {
    setScanning(true);
    try {
      const res = await axios.post(`${API}/used-vehicles/scan-sessions`);
      setScanSession(res.data);
      // Start polling for results
      pollRef.current = setInterval(async () => {
        try {
          const poll = await axios.get(`${API}/used-vehicles/scan-sessions/${res.data.id}`);
          if (poll.data.status === 'completed' && poll.data.result) {
            clearInterval(pollRef.current);
            // Auto-fill form
            setForm(prev => {
              const updated = { ...prev };
              Object.keys(poll.data.result).forEach(key => {
                if (poll.data.result[key] !== '' && poll.data.result[key] !== 0 && poll.data.result[key] !== null) {
                  updated[key] = poll.data.result[key];
                }
              });
              return updated;
            });
            setScanSession(null);
            setScanning(false);
            setActiveTab('vehicule');
            toast.success('Carte grise scannee ! Les champs ont ete remplis.');
          } else if (poll.data.status === 'processing') {
            setScanSession(prev => prev ? { ...prev, status: 'processing' } : prev);
          } else if (poll.data.status === 'error') {
            clearInterval(pollRef.current);
            setScanSession(null);
            setScanning(false);
            toast.error(poll.data.error || 'Erreur lors du scan');
          }
        } catch (err) {
          console.warn('Polling scan session error:', err.message);
        }
      }, 2000);
    } catch (err) {
      setScanning(false);
      toast.error('Erreur de création de session');
    }
  };

  const cancelScan = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    setScanSession(null);
    setScanning(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.brand || !form.model) {
      toast.error('Marque et modèle sont obligatoires');
      return;
    }
    try {
      const payload = { ...form };
      ['seats_total','seats_front','displacement_cc','empty_weight_kg','total_weight_kg','payload_kg','towing_weight_kg','co2_emissions','mileage'].forEach(k => { payload[k] = parseInt(payload[k]) || 0; });
      ['power_kw','purchase_price','selling_price','commission_percent','commission_fixed','body_cost_indicative','mech_cost_indicative'].forEach(k => { payload[k] = parseFloat(payload[k]) || 0; });

      if (editing) {
        await axios.put(`${API}/used-vehicles/${editing.id}`, payload);
        toast.success('Véhicule modifié');
      } else {
        await axios.post(`${API}/used-vehicles`, payload);
        toast.success('Véhicule ajouté');
      }
      setShowDialog(false);
      resetForm();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleEdit = (v) => {
    setEditing(v);
    const f = { ...EMPTY_FORM };
    Object.keys(f).forEach(k => { if (v[k] !== undefined && v[k] !== null) f[k] = v[k]; });
    setForm(f);
    setActiveTab('vehicule');
    setShowDialog(true);
  };

  const handleDeactivate = async (id) => {
    if (!window.confirm('Retirer ce véhicule de la liste ?')) return;
    try {
      await axios.delete(`${API}/used-vehicles/${id}`);
      toast.success('Véhicule retiré');
      load();
    } catch { toast.error('Erreur'); }
  };

  const resetForm = () => { setEditing(null); setForm({ ...EMPTY_FORM }); setActiveTab('vehicule'); };

  const deletePendingContract = async (contractId) => {
    if (!window.confirm('Supprimer ce contrat en attente ? Le vehicule retournera en stock.')) return;
    try {
      await axios.delete(`${API}/sale-contracts/${contractId}`);
      toast.success('Contrat supprime');
      load(); loadPending();
    } catch { toast.error('Erreur de suppression'); }
  };

  const concludePendingContract = async (contractId) => {
    if (!window.confirm('Conclure cette vente ? Le vehicule sera marque comme vendu.')) return;
    try {
      await axios.post(`${API}/sale-contracts/${contractId}/conclude`);
      toast.success('Contrat conclu ! Vehicule vendu.');
      load(); loadSold(); loadPending();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const openPendingContract = (vehicle, contract) => {
    setSaleContract(contract);
    setSaleVehicle(vehicle);
  };

  const filtered = vehicles.filter(v => {
    if (v.status === 'pret_a_livrer') return false;
    if (statusFilter !== 'all' && v.status !== statusFilter) return false;
    if (categoryFilter !== 'all' && (v.category || 'nos_vehicules') !== categoryFilter) return false;
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return `${v.brand} ${v.model}`.toLowerCase().includes(q) ||
      (v.plate_number || '').toLowerCase().includes(q) ||
      (v.chassis_number || '').toLowerCase().includes(q) ||
      (v.color || '').toLowerCase().includes(q);
  });

  const pretALivrerVehicles = vehicles.filter(v => v.status === 'pret_a_livrer');

  const tabIdx = TABS.findIndex(t => t.id === activeTab);
  const goNext = () => { if (tabIdx < TABS.length - 1) setActiveTab(TABS[tabIdx + 1].id); };
  const goPrev = () => { if (tabIdx > 0) setActiveTab(TABS[tabIdx - 1].id); };

  const field = (label, key, type = 'text', props = {}) => (
    <div className="space-y-1.5">
      <Label className="text-xs font-medium text-slate-600">{label}</Label>
      <Input type={type} value={form[key] ?? ''} onChange={e => set(key, e.target.value)}
        className="h-9 text-sm" data-testid={`uv-field-${key}`} {...props} />
    </div>
  );

  const sel = (label, key, options, placeholder = 'Choisir...') => (
    <div className="space-y-1.5">
      <Label className="text-xs font-medium text-slate-600">{label}</Label>
      <Select value={form[key] || ''} onValueChange={v => set(key, v)}>
        <SelectTrigger className="h-9 text-sm" data-testid={`uv-field-${key}`}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map(o => (
            <SelectItem key={typeof o === 'string' ? o : o.value} value={typeof o === 'string' ? o : o.value}>
              {typeof o === 'string' ? o : o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  const formatPrice = (p) => p ? `CHF ${Number(p).toLocaleString('fr-CH')}.-` : '-';

  return (
    <AdminLayout auth={auth} onLogout={onLogout}>
      <div className="p-4 md:p-8 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-4xl font-heading font-bold text-slate-900 tracking-tight" data-testid="used-vehicles-title">
              Véhicules d'occasion
            </h1>
            <div className="flex gap-2 mt-2">
              <Button size="sm" variant={viewTab === 'stock' ? 'default' : 'outline'} onClick={() => setViewTab('stock')} data-testid="tab-stock">
                En stock ({filtered.length})
              </Button>
              <Button size="sm" variant={viewTab === 'pret_a_livrer' ? 'default' : 'outline'} onClick={() => setViewTab('pret_a_livrer')}
                className={viewTab === 'pret_a_livrer' ? 'bg-blue-600 hover:bg-blue-700' : 'text-blue-600 border-blue-200 hover:bg-blue-50'}
                data-testid="tab-pret-a-livrer">
                Pret a livrer ({pretALivrerVehicles.length})
              </Button>
              <Button size="sm" variant={viewTab === 'pending' ? 'default' : 'outline'} onClick={() => setViewTab('pending')}
                className={viewTab === 'pending' ? 'bg-red-600 hover:bg-red-700' : 'text-red-600 border-red-200 hover:bg-red-50'}
                data-testid="tab-pending">
                En attente de signature ({pendingVehicles.length})
              </Button>
              <Button size="sm" variant={viewTab === 'vendu' ? 'default' : 'outline'} onClick={() => setViewTab('vendu')} className={viewTab === 'vendu' ? 'bg-emerald-600 hover:bg-emerald-700' : ''} data-testid="tab-vendu">
                Vendus ({soldVehicles.length})
              </Button>
            </div>
          </div>
          {viewTab === 'stock' && (
          <Dialog open={showDialog} onOpenChange={open => { setShowDialog(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="btn-primary rounded-md" data-testid="add-used-vehicle-btn">
                <Plus className="mr-2 w-5 h-5" /> Nouveau véhicule
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
              <DialogHeader>
                <DialogTitle>{editing ? 'Modifier le véhicule' : 'Nouveau véhicule'}</DialogTitle>
              </DialogHeader>

              {/* Scanner QR code workflow */}
              {!editing && (
                <div className="border-2 border-dashed border-blue-200 rounded-xl p-4 bg-blue-50/50 mb-2">
                  {scanSession ? (
                    <div className="space-y-3 text-center">
                      <p className="text-sm font-semibold text-blue-700">Scannez ce QR code avec votre téléphone</p>
                      <img src={scanSession.qr_code} alt="QR Code" className="mx-auto w-48 h-48 rounded-lg border border-slate-200" data-testid="scan-qr-code" />
                      <p className="text-xs text-blue-500">
                        {scanSession.status === 'processing' ? 'Traitement de la photo en cours...' : 'En attente de la photo...'}
                      </p>
                      <div className="flex justify-center">
                        <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
                      </div>
                      <button type="button" onClick={cancelScan} className="text-xs text-slate-500 hover:text-slate-700 underline" data-testid="cancel-scan-btn">
                        Annuler
                      </button>
                    </div>
                  ) : scanning ? (
                    <div className="flex items-center justify-center gap-3 py-2">
                      <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
                      <span className="text-sm font-medium text-blue-700">Préparation du scan...</span>
                    </div>
                  ) : (
                    <button type="button" onClick={startScanSession}
                      className="w-full flex items-center justify-center gap-3 py-2 text-blue-700 hover:text-blue-800 transition-colors"
                      data-testid="scan-carte-grise-btn">
                      <Camera className="w-6 h-6" />
                      <div className="text-left">
                        <p className="text-sm font-semibold">Scanner la carte grise</p>
                        <p className="text-xs text-blue-500">Scannez le QR code avec votre téléphone pour photographier le permis</p>
                      </div>
                    </button>
                  )}
                </div>
              )}

              {/* Category selector */}
              <div className="flex gap-2 mb-2">
                <button onClick={() => set('category', 'nos_vehicules')}
                  className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium border-2 transition-colors ${
                    form.category === 'nos_vehicules' ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-slate-200 text-slate-500 hover:border-slate-300'
                  }`} data-testid="cat-nos-vehicules">
                  <Car className="w-4 h-4 inline mr-1.5" />Nos véhicules
                </button>
                <button onClick={() => set('category', 'depot_vente')}
                  className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium border-2 transition-colors ${
                    form.category === 'depot_vente' ? 'border-amber-600 bg-amber-50 text-amber-700' : 'border-slate-200 text-slate-500 hover:border-slate-300'
                  }`} data-testid="cat-depot-vente">
                  <Handshake className="w-4 h-4 inline mr-1.5" />Dépôt-vente
                </button>
              </div>

              {/* Depot-vente specific fields */}
              {form.category === 'depot_vente' && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-3 mb-2">
                  <p className="text-xs font-semibold text-amber-700 uppercase">Informations dépôt-vente</p>
                  <div className="grid grid-cols-2 gap-3">
                    {field('Nom du déposant', 'deposant_name')}
                    {field('Téléphone déposant', 'deposant_phone', 'tel')}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {field('Commission (%)', 'commission_percent', 'number', { step: 0.5, min: 0, max: 100 })}
                    {field('Commission fixe (CHF)', 'commission_fixed', 'number', { step: 100 })}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {field('Date de dépôt', 'deposit_date', 'date')}
                    {field('Fin du contrat', 'contract_end_date', 'date')}
                  </div>
                </div>
              )}

              {/* Tabs */}
              <div className="flex gap-1 border-b border-slate-200 overflow-x-auto pb-0" data-testid="uv-form-tabs">
                {TABS.map(tab => {
                  const Icon = tab.icon;
                  return (
                    <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                      className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 transition-colors whitespace-nowrap ${
                        activeTab === tab.id ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'
                      }`} data-testid={`uv-tab-${tab.id}`}>
                      <Icon className="w-3.5 h-3.5" />{tab.label}
                    </button>
                  );
                })}
              </div>

              <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-1 py-3 space-y-4">
                {activeTab === 'vehicule' && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      {field('Marque *', 'brand')}
                      {field('Modèle *', 'model')}
                    </div>
                    {field('Plaque d\'immatriculation (15)', 'plate_number', 'text', { placeholder: 'NE 73486' })}
                    <div className="grid grid-cols-2 gap-3">
                      {sel('Genre du véhicule (19)', 'vehicle_genre', VEHICLE_GENRES)}
                      {sel('Carrosserie (23)', 'body_type', BODY_TYPES)}
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      {field('Couleur (25)', 'color')}
                      {field('Code couleur', 'color_code')}
                      {field('Usage spécial (17)', 'special_usage')}
                    </div>
                    {field('Numéro de châssis / VIN (E/23)', 'chassis_number', 'text', { placeholder: 'WV WZZ Z1J 22B 013 177' })}
                  </div>
                )}

                {activeTab === 'technique' && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      {field('N. de matricule (18)', 'serial_number')}
                      {field('Réception par type (24)', 'type_approval')}
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      {field('Cylindrée cm\u00B3 (37)', 'displacement_cc', 'number')}
                      {field('Puissance kW (76)', 'power_kw', 'number', { step: 0.01 })}
                      {field('Émissions CO\u2082 g/km (14)', 'co2_emissions', 'number')}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      {field('Places total (27)', 'seats_total', 'number')}
                      {field('Places avant (28)', 'seats_front', 'number')}
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      {field('Poids à vide kg (36)', 'empty_weight_kg', 'number')}
                      {field('Poids total kg (33)', 'total_weight_kg', 'number')}
                      {field('Charge utile kg (32)', 'payload_kg', 'number')}
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      {field('Remorquable kg (35)', 'towing_weight_kg', 'number')}
                      {field('Code émission (72)', 'emission_code')}
                      {field('Code véhicule (59)', 'vehicle_code')}
                    </div>
                  </div>
                )}

                {activeTab === 'detenteur' && (
                  <div className="space-y-4">
                    <p className="text-sm font-semibold text-slate-700">Ancien détenteur (Section C)</p>
                    {field('Nom, prénoms (01)', 'owner_name')}
                    {field('Rue et numéro', 'owner_street')}
                    <div className="grid grid-cols-3 gap-3">
                      {field('NPA', 'owner_postal_code')}
                      <div className="col-span-2">{field('Ville', 'owner_city')}</div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      {field('Date de naissance (07)', 'owner_dob', 'date')}
                      {field('Pays d\'origine (08)', 'owner_origin')}
                    </div>
                    <div className="border-t border-slate-200 pt-4">
                      <p className="text-sm font-semibold text-slate-700 mb-3">Assurance (09)</p>
                      <div className="grid grid-cols-2 gap-3">
                        {field('Nom de l\'assurance', 'insurance_name')}
                        {field('N° police', 'insurance_number')}
                      </div>
                    </div>
                    {field('Annotations cantonales (13)', 'canton_notes')}
                  </div>
                )}

                {activeTab === 'admin' && (
                  <div className="space-y-4">
                    {field('Première mise en circulation (39)', 'first_registration', 'date')}
                    <div className="grid grid-cols-2 gap-3">
                      {field('Date expertise', 'expertise_date', 'date')}
                      {sel('Canton expertise', 'expertise_canton', CANTONS.map(c => ({ value: c, label: c })))}
                    </div>
                    {field('Kilométrage actuel', 'mileage', 'number', { placeholder: '85000' })}
                  </div>
                )}

                {activeTab === 'vente' && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      {field('Prix d\'achat (CHF)', 'purchase_price', 'number', { step: 100 })}
                      {field('Prix de vente (CHF)', 'selling_price', 'number', { step: 100 })}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      {sel('Statut', 'status', [
                        { value: 'disponible', label: 'Disponible' },
                        { value: 'reserve', label: 'Réservé' },
                        { value: 'vendu', label: 'Vendu' },
                      ])}
                      {sel('État', 'condition', [
                        { value: 'excellent', label: 'Excellent' },
                        { value: 'bon', label: 'Bon' },
                        { value: 'moyen', label: 'Moyen' },
                        { value: 'a_reviser', label: 'À réviser' },
                      ])}
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium text-slate-600">Description</Label>
                      <Textarea value={form.description} onChange={e => set('description', e.target.value)}
                        placeholder="Description du véhicule, équipements, remarques..."
                        rows={4} className="text-sm" data-testid="uv-field-description" />
                    </div>

                    {/* Frais indicatifs de remise en état */}
                    <div className="border-t border-slate-200 pt-4">
                      <h4 className="text-sm font-bold text-slate-700 mb-3" data-testid="uv-refurb-title">Frais indicatifs de remise en etat</h4>
                      <div className="grid grid-cols-2 gap-3">
                        {sel('Carrosserie', 'body_state', [
                          { value: 'bon', label: 'Bon' },
                          { value: 'moyen', label: 'Moyen' },
                          { value: 'a_faire', label: 'A faire (voir photos)' },
                        ])}
                        {form.body_state === 'a_faire' && field('Frais carrosserie (CHF)', 'body_cost_indicative', 'number', { step: 100 })}
                      </div>
                      {(form.body_state === 'moyen' || form.body_state === 'a_faire') && (
                        <div className="mt-2">
                          <Textarea value={form.body_details || ''} onChange={e => set('body_details', e.target.value)}
                            placeholder="Details carrosserie : rayures, bosses, peinture..."
                            rows={2} className="text-sm" data-testid="uv-field-body-details" />
                        </div>
                      )}
                      <div className="grid grid-cols-2 gap-3 mt-3">
                        {sel('Mecanique', 'mech_state', [
                          { value: 'service_a_jour', label: 'Service a jour' },
                          { value: 'frais_a_prevoir', label: 'Frais a prevoir' },
                        ])}
                        {form.mech_state === 'frais_a_prevoir' && field('Frais mecanique (CHF)', 'mech_cost_indicative', 'number', { step: 100 })}
                      </div>
                      {form.mech_state === 'frais_a_prevoir' && (
                        <div className="mt-2">
                          <Textarea value={form.mech_details || ''} onChange={e => set('mech_details', e.target.value)}
                            placeholder="Details mecanique : courroie, freins, embrayage..."
                            rows={2} className="text-sm" data-testid="uv-field-mech-details" />
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-between pt-3 border-t border-slate-200">
                  <Button type="button" variant="outline" size="sm" onClick={goPrev}
                    disabled={tabIdx === 0} className="text-xs">
                    <ChevronLeft className="w-4 h-4 mr-1" /> Précédent
                  </Button>
                  {tabIdx === TABS.length - 1 ? (
                    <Button type="submit" className="btn-primary text-sm" data-testid="save-used-vehicle-btn">
                      {editing ? 'Modifier' : 'Ajouter le véhicule'}
                    </Button>
                  ) : (
                    <Button type="button" size="sm" onClick={goNext} className="text-xs bg-blue-600 hover:bg-blue-700 text-white">
                      Suivant <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  )}
                </div>
              </form>
            </DialogContent>
          </Dialog>
          )}
        </div>

        {viewTab === 'stock' && (<>
        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Rechercher par marque, modèle, plaque, couleur..."
              value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              className="pl-9 h-10" data-testid="search-used-vehicles" />
          </div>
          <div className="flex gap-2">
            {['all', 'nos_vehicules', 'depot_vente'].map(c => (
              <Button key={c} variant={categoryFilter === c ? 'default' : 'outline'} size="sm"
                onClick={() => setCategoryFilter(c)}
                className={categoryFilter === c ? 'bg-blue-600 text-white' : ''}
                data-testid={`cat-filter-${c}`}>
                {c === 'all' ? 'Tous' : c === 'nos_vehicules' ? 'Nos véhicules' : 'Dépôt-vente'}
              </Button>
            ))}
          </div>
          <div className="flex gap-2">
            {['all', 'disponible', 'reserve', 'vendu'].map(s => (
              <Button key={s} variant={statusFilter === s ? 'default' : 'outline'} size="sm"
                onClick={() => setStatusFilter(s)}
                className={statusFilter === s ? (s === 'all' ? 'bg-slate-700 text-white' : 'bg-blue-600 text-white') : ''}
                data-testid={`filter-${s}`}>
                {s === 'all' ? 'Tous' : STATUS_MAP[s]?.label || s}
              </Button>
            ))}
          </div>
        </div>

        {/* Detail modal */}
        {showDetail && (
          <Dialog open={!!showDetail} onOpenChange={() => setShowDetail(null)}>
            <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{showDetail.brand} {showDetail.model}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 text-sm">
                {showDetail.category === 'depot_vente' && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <Badge className="bg-amber-100 text-amber-700 mb-2">Dépôt-vente</Badge>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div><span className="text-slate-500">Déposant:</span> <span className="font-medium">{showDetail.deposant_name || '-'}</span></div>
                      <div><span className="text-slate-500">Tél:</span> <span className="font-medium">{showDetail.deposant_phone || '-'}</span></div>
                      <div><span className="text-slate-500">Commission:</span> <span className="font-medium">{showDetail.commission_percent > 0 ? `${showDetail.commission_percent}%` : showDetail.commission_fixed > 0 ? `CHF ${showDetail.commission_fixed}` : '-'}</span></div>
                      <div><span className="text-slate-500">Fin contrat:</span> <span className="font-medium">{showDetail.contract_end_date || '-'}</span></div>
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-2">
                  <div><span className="text-slate-500">Plaque:</span> <span className="font-medium">{showDetail.plate_number || '-'}</span></div>
                  <div><span className="text-slate-500">Couleur:</span> <span className="font-medium">{showDetail.color || '-'}</span></div>
                  <div><span className="text-slate-500">Carrosserie:</span> <span className="font-medium">{showDetail.body_type || '-'}</span></div>
                  <div><span className="text-slate-500">Genre:</span> <span className="font-medium">{showDetail.vehicle_genre || '-'}</span></div>
                  <div><span className="text-slate-500">Châssis:</span> <span className="font-medium text-xs">{showDetail.chassis_number || '-'}</span></div>
                  <div><span className="text-slate-500">Km:</span> <span className="font-medium">{showDetail.mileage?.toLocaleString('fr-CH') || '0'}</span></div>
                  <div><span className="text-slate-500">Cylindrée:</span> <span className="font-medium">{showDetail.displacement_cc} cm³</span></div>
                  <div><span className="text-slate-500">Puissance:</span> <span className="font-medium">{showDetail.power_kw} kW</span></div>
                  <div><span className="text-slate-500">CO²:</span> <span className="font-medium">{showDetail.co2_emissions} g/km</span></div>
                  <div><span className="text-slate-500">1ère mise en circ.:</span> <span className="font-medium">{showDetail.first_registration || '-'}</span></div>
                </div>
                <div className="border-t pt-3 grid grid-cols-2 gap-2">
                  <div><span className="text-slate-500">Ancien propriétaire:</span> <span className="font-medium">{showDetail.owner_name || '-'}</span></div>
                  <div><span className="text-slate-500">Assurance:</span> <span className="font-medium">{showDetail.insurance_name || '-'}</span></div>
                </div>
                <div className="border-t pt-3 grid grid-cols-2 gap-2">
                  <div><span className="text-slate-500">Prix achat:</span> <span className="font-medium">{formatPrice(showDetail.purchase_price)}</span></div>
                  <div><span className="text-slate-500">Prix vente:</span> <span className="font-bold text-blue-700">{formatPrice(showDetail.selling_price)}</span></div>
                  <div><span className="text-slate-500">État:</span> <span className="font-medium">{CONDITION_MAP[showDetail.condition] || '-'}</span></div>
                </div>
                {/* Frais indicatifs */}
                {(showDetail.body_state || showDetail.mech_state) && (
                  <div className="border-t pt-3" data-testid="uv-refurb-display">
                    <p className="text-slate-500 mb-2 font-semibold text-sm">Frais indicatifs de remise en etat :</p>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      {showDetail.body_state && (
                        <div className="bg-slate-50 rounded p-2">
                          <span className="text-slate-500">Carrosserie : </span>
                          <span className={`font-medium ${showDetail.body_state === 'bon' ? 'text-green-600' : showDetail.body_state === 'moyen' ? 'text-orange-500' : 'text-red-500'}`}>
                            {showDetail.body_state === 'bon' ? 'Bon' : showDetail.body_state === 'moyen' ? 'Moyen' : 'A faire'}
                          </span>
                          {showDetail.body_cost_indicative > 0 && <span className="text-red-600 font-bold ml-1">CHF {Number(showDetail.body_cost_indicative).toLocaleString('fr-CH')}</span>}
                          {showDetail.body_details && <p className="text-xs text-slate-400 mt-1">{showDetail.body_details}</p>}
                        </div>
                      )}
                      {showDetail.mech_state && (
                        <div className="bg-slate-50 rounded p-2">
                          <span className="text-slate-500">Mecanique : </span>
                          <span className={`font-medium ${showDetail.mech_state === 'service_a_jour' ? 'text-green-600' : 'text-orange-500'}`}>
                            {showDetail.mech_state === 'service_a_jour' ? 'Service a jour' : 'Frais a prevoir'}
                          </span>
                          {showDetail.mech_cost_indicative > 0 && <span className="text-red-600 font-bold ml-1">CHF {Number(showDetail.mech_cost_indicative).toLocaleString('fr-CH')}</span>}
                          {showDetail.mech_details && <p className="text-xs text-slate-400 mt-1">{showDetail.mech_details}</p>}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {/* === COUT DE REVIENT === */}
                <CostSection vehicle={showDetail} onUpdate={(updated) => { setShowDetail(updated); load(); }} />
                {showDetail.description && (
                  <div className="border-t pt-3">
                    <p className="text-slate-500 mb-1">Description:</p>
                    <p>{showDetail.description}</p>
                  </div>
                )}
                {/* Contract buttons */}
                <div className="border-t pt-3 flex gap-2 flex-wrap">
                  <Button size="sm" className="flex-1 text-xs bg-blue-600 hover:bg-blue-700 text-white"
                    onClick={() => { setShowDetail(null); setSaleContract(null); setSaleVehicle(showDetail); }}
                    data-testid="btn-contrat-vente">
                    <FileText className="w-3.5 h-3.5 mr-1" /> Contrat de vente
                  </Button>
                  {showDetail.category === 'depot_vente' && (
                    <Button size="sm" variant="outline" className="flex-1 text-xs text-amber-700 border-amber-300 hover:bg-amber-50"
                      onClick={async () => {
                        try {
                          const { downloadPdf } = await import('@/utils/api');
                          await downloadPdf(`${API}/used-vehicles/${showDetail.id}/contrat-depot-vente`, `contrat-depot-vente-${showDetail.id}.pdf`);
                        } catch { toast.error('Erreur telechargement'); }
                      }}
                      data-testid="btn-contrat-depot-vente">
                      <Download className="w-3.5 h-3.5 mr-1" /> Contrat depot-vente
                    </Button>
                  )}
                </div>
                {/* Preparation buttons */}
                <div className="border-t pt-3 flex gap-2 flex-wrap">
                  <Button size="sm" variant="outline"
                    className={`flex-1 text-xs ${prepStatuses[showDetail.id]?.parc?.completed ? 'text-emerald-600 border-emerald-300 bg-emerald-50' : 'text-blue-600 border-blue-200 hover:bg-blue-50'}`}
                    onClick={() => { setShowDetail(null); setPrepDialog({ open: true, vehicleId: showDetail.id, vehicleName: `${showDetail.brand} ${showDetail.model}`, type: 'parc' }); }}
                    data-testid="detail-prep-parc-btn">
                    {prepStatuses[showDetail.id]?.parc?.completed ? <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> : <ClipboardList className="w-3.5 h-3.5 mr-1" />}
                    Prép. parc occasion
                  </Button>
                  <Button size="sm" variant="outline"
                    className={`flex-1 text-xs ${prepStatuses[showDetail.id]?.livraison?.completed ? 'text-emerald-600 border-emerald-300 bg-emerald-50' : 'text-amber-600 border-amber-200 hover:bg-amber-50'}`}
                    onClick={() => { setShowDetail(null); setPrepDialog({ open: true, vehicleId: showDetail.id, vehicleName: `${showDetail.brand} ${showDetail.model}`, type: 'livraison' }); }}
                    data-testid="detail-prep-livraison-btn">
                    {prepStatuses[showDetail.id]?.livraison?.completed ? <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> : <Truck className="w-3.5 h-3.5 mr-1" />}
                    Prép. livraison
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* Vehicle Cards */}
        {loading ? (
          <div className="text-center py-12"><p className="text-slate-500">Chargement...</p></div>
        ) : filtered.length === 0 ? (
          <Card className="p-12 text-center rounded-xl">
            <Car className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500">Aucun véhicule</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filtered.map(v => {
              const st = STATUS_MAP[v.status] || STATUS_MAP.disponible;
              const isDepot = v.category === 'depot_vente';
              return (
                <Card key={v.id} className="p-0 rounded-xl border border-slate-200 shadow-sm overflow-hidden hover:shadow-md transition-shadow"
                  data-testid={`used-vehicle-card-${v.id}`}>
                  <div className={`h-2 bg-gradient-to-r ${isDepot ? 'from-amber-400 to-amber-600' : 'from-blue-500 to-blue-700'}`} />
                  <div className="p-4 space-y-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-bold text-lg text-slate-900">{v.brand} {v.model}</h3>
                        <p className="text-sm text-slate-500">{v.body_type}{v.color ? ` - ${v.color}` : ''}</p>
                      </div>
                      <div className="flex flex-col gap-1 items-end">
                        <Badge className={`${st.color} text-xs font-medium`}>{st.label}</Badge>
                        {isDepot && <Badge className="bg-amber-100 text-amber-700 text-xs">Dépôt</Badge>}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-500">Plaque</span>
                        <span className="font-mono font-medium">{v.plate_number || '-'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Km</span>
                        <span className="font-mono">{v.mileage?.toLocaleString('fr-CH') || '0'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Année</span>
                        <span>{v.first_registration || '-'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">État</span>
                        <span>{CONDITION_MAP[v.condition] || '-'}</span>
                      </div>
                    </div>

                    {v.selling_price > 0 && (
                      <div className="text-right">
                        <span className="text-xl font-bold text-blue-700">{formatPrice(v.selling_price)}</span>
                      </div>
                    )}

                    <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
                      <Button size="sm" variant="outline" className="flex-1 text-xs" onClick={() => setShowDetail(v)}
                        data-testid={`view-${v.id}`}>
                        <Eye className="w-3.5 h-3.5 mr-1" /> Détails
                      </Button>
                      <Button size="sm" variant="outline" className="text-xs text-indigo-600 border-indigo-200 hover:bg-indigo-50"
                        onClick={() => setPublishVehicle(v)}
                        title="Publier sur la Marketplace"
                        data-testid={`publish-marketplace-${v.id}`}>
                        <ShoppingCart className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="outline" className={`text-xs ${prepStatuses[v.id]?.parc?.completed ? 'text-emerald-600 border-emerald-300 bg-emerald-50' : 'text-blue-600 border-blue-200 hover:bg-blue-50'}`}
                        onClick={() => setPrepDialog({ open: true, vehicleId: v.id, vehicleName: `${v.brand} ${v.model}`, type: 'parc' })}
                        title="Fiche préparation parc occasion"
                        data-testid={`prep-parc-${v.id}`}>
                        {prepStatuses[v.id]?.parc?.completed ? <CheckCircle2 className="w-3.5 h-3.5" /> : <ClipboardList className="w-3.5 h-3.5" />}
                      </Button>
                      <Button size="sm" variant="outline" className={`text-xs ${prepStatuses[v.id]?.livraison?.completed ? 'text-emerald-600 border-emerald-300 bg-emerald-50' : 'text-amber-600 border-amber-200 hover:bg-amber-50'}`}
                        onClick={() => setPrepDialog({ open: true, vehicleId: v.id, vehicleName: `${v.brand} ${v.model}`, type: 'livraison' })}
                        title="Fiche préparation livraison"
                        data-testid={`prep-livraison-${v.id}`}>
                        {prepStatuses[v.id]?.livraison?.completed ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Truck className="w-3.5 h-3.5" />}
                      </Button>
                      <Button size="sm" variant="outline" className="text-xs" onClick={() => handleEdit(v)}
                        data-testid={`edit-${v.id}`}>
                        <Edit className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="outline" className="text-xs text-red-600 hover:bg-red-50"
                        onClick={() => handleDeactivate(v.id)} data-testid={`delete-${v.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
        </>)}

        {/* PRET A LIVRER TAB */}
        {viewTab === 'pret_a_livrer' && (
          <div>
            {pretALivrerVehicles.length === 0 ? (
              <div className="text-center py-12">
                <Truck className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p className="text-slate-500">Aucun vehicule pret a livrer</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {pretALivrerVehicles.map(v => {
                  const status = STATUS_MAP[v.status] || { label: v.status, color: 'bg-slate-100' };
                  return (
                    <div key={v.id} className="bg-white rounded-xl border border-blue-200 overflow-hidden hover:shadow-md transition-shadow" data-testid={`pret-vehicle-${v.id}`}>
                      <div className="p-4">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="font-bold text-slate-900">{v.brand} {v.model}</h3>
                          <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${status.color}`}>{status.label}</span>
                        </div>
                        <div className="text-sm text-slate-500 space-y-1">
                          {v.plate_number && <p>Plaque : {v.plate_number}</p>}
                          {v.color && <p>Couleur : {v.color}</p>}
                          {v.mileage > 0 && <p>Km : {Number(v.mileage).toLocaleString('fr-CH')}</p>}
                          {v.selling_price > 0 && <p className="font-semibold text-blue-700">Prix : CHF {Number(v.selling_price).toLocaleString('fr-CH')}</p>}
                        </div>
                        <div className="flex gap-2 mt-3">
                          <Button size="sm" variant="outline" className="text-xs text-blue-600"
                            onClick={() => setPrepDialog({ open: true, vehicleId: v.id, vehicleName: `${v.brand} ${v.model}`, type: 'livraison' })}
                            data-testid={`prep-livraison-ready-${v.id}`}>
                            <Truck className="w-3.5 h-3.5 mr-1" /> Voir la preparation
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* PENDING SIGNATURE TAB */}
        {viewTab === 'pending' && (
          <div>
            {pendingVehicles.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p className="text-slate-500">Aucun contrat en attente de signature</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {pendingVehicles.map(v => {
                  const contract = pendingContracts.find(c => c.vehicle_id === v.id);
                  return (
                    <Card key={v.id} className="overflow-hidden border-red-200" data-testid={`pending-${v.id}`}>
                      <div className="h-2 bg-gradient-to-r from-red-400 to-red-600" />
                      <div className="p-4 space-y-3">
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="font-bold text-lg text-slate-900">{v.brand} {v.model}</h3>
                            <p className="text-sm text-slate-500">{v.plate_number}</p>
                          </div>
                          <Badge className="bg-red-100 text-red-700 text-xs">{t('common.pending')}</Badge>
                        </div>
                        {contract && (
                          <div className="text-sm space-y-1">
                            <div className="flex justify-between">
                              <span className="text-slate-500">Client</span>
                              <span className="font-medium">{contract.client_name}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-500">Prix TTC</span>
                              <span className="font-mono">{formatPrice(contract.price_ttc)}</span>
                            </div>
                            {contract.down_payment_amount > 0 && (
                              <div className="flex justify-between">
                                <span className="text-slate-500">Acompte</span>
                                <span className="font-mono text-emerald-600">{formatPrice(contract.down_payment_amount)}</span>
                              </div>
                            )}
                            <div className="flex justify-between font-bold border-t pt-1">
                              <span>Total</span>
                              <span className="text-red-700">{formatPrice(contract.total)}</span>
                            </div>
                            <div className="text-xs text-slate-400">{contract.created_at && new Date(contract.created_at).toLocaleDateString('fr-CH')}</div>
                          </div>
                        )}
                        <div className="flex gap-2 pt-2 border-t border-slate-100">
                          {contract && (
                            <>
                              <Button size="sm" variant="outline" className="flex-1 text-xs"
                                onClick={() => openPendingContract(v, contract)}
                                data-testid={`edit-pending-${v.id}`}>
                                <Edit className="w-3.5 h-3.5 mr-1" /> Modifier
                              </Button>
                              <Button size="sm" className="flex-1 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                                onClick={() => concludePendingContract(contract.id)}
                                data-testid={`conclude-pending-${v.id}`}>
                                <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Conclure
                              </Button>
                              <Button size="sm" variant="outline" className="text-xs"
                                onClick={async () => {
                                  const p = new URLSearchParams();
                                  if (contract.client_id) p.set('client_id', contract.client_id);
                                  if (contract.warranty) { p.set('warranty_months', contract.warranty.months || 0); p.set('warranty_details', `${contract.warranty.label || ''} - CHF ${contract.warranty.price || 0}`); }
                                  p.set('price_ht', contract.price_ht || 0);
                                  p.set('price_ttc', contract.price_ttc || 0);
                                  if (contract.delivery_price > 0) p.set('delivery_price', contract.delivery_price);
                                  if (contract.offered_items?.length > 0) p.set('offered_items', contract.offered_items.join(', '));
                                  if (contract.down_payment_amount > 0) {
                                    p.set('down_payment_amount', contract.down_payment_amount);
                                    if (contract.down_payment_method) p.set('down_payment_method', contract.down_payment_method);
                                  }
                                  if (contract.trade_in && contract.trade_in.brand) {
                                    p.set('tradein_brand', contract.trade_in.brand || '');
                                    p.set('tradein_model', contract.trade_in.model || '');
                                    p.set('tradein_plate', contract.trade_in.plate_number || '');
                                    p.set('tradein_chassis', contract.trade_in.chassis_number || '');
                                    p.set('tradein_color', contract.trade_in.color || '');
                                    p.set('tradein_first_reg', contract.trade_in.first_registration || '');
                                    if (contract.trade_in.mileage) p.set('tradein_km', contract.trade_in.mileage);
                                    if (contract.trade_in.value) p.set('tradein_value', contract.trade_in.value);
                                    if (contract.trade_in.expertise_date) p.set('tradein_expertise', contract.trade_in.expertise_date);
                                  }
                                  const { downloadPdf } = await import('@/utils/api');
                                  await downloadPdf(`${API}/used-vehicles/${v.id}/contrat-vente?${p.toString()}`, `contrat-vente-${v.id}.pdf`).catch(() => toast.error('Erreur telechargement'));
                                }}
                                data-testid={`download-pending-contract-${v.id}`}>
                                <Download className="w-3.5 h-3.5" />
                              </Button>
                              <Button size="sm" variant="outline" className="text-xs text-red-600 hover:bg-red-50 border-red-200"
                                onClick={() => deletePendingContract(contract.id)}
                                data-testid={`delete-pending-${v.id}`}>
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </>
                          )}
                          {!contract && (
                            <Button size="sm" variant="outline" className="flex-1 text-xs" onClick={() => setShowDetail(v)}
                              data-testid={`view-pending-${v.id}`}>
                              <Eye className="w-3.5 h-3.5 mr-1" /> Details
                            </Button>
                          )}
                        </div>
                        {/* Preparation livraison button for pending vehicles */}
                        <div className="flex gap-2 pt-1">
                          <Button size="sm" variant="outline"
                            className={`flex-1 text-xs ${prepStatuses[v.id]?.livraison?.completed ? 'text-emerald-600 border-emerald-300 bg-emerald-50' : 'text-amber-600 border-amber-200 hover:bg-amber-50'}`}
                            onClick={() => setPrepDialog({ open: true, vehicleId: v.id, vehicleName: `${v.brand} ${v.model}`, type: 'livraison' })}
                            data-testid={`prep-livraison-pending-${v.id}`}>
                            {prepStatuses[v.id]?.livraison?.completed ? <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> : <Truck className="w-3.5 h-3.5 mr-1" />}
                            Prép. livraison
                          </Button>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* SOLD VEHICLES TAB */}
        {viewTab === 'vendu' && (
          <div>
            {soldVehicles.length === 0 ? (
              <div className="text-center py-12">
                <Car className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p className="text-slate-500">Aucun vehicule vendu</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {soldVehicles.map(v => {
                  const contract = soldContracts.find(c => c.vehicle_id === v.id);
                  return (
                    <Card key={v.id} className="overflow-hidden" data-testid={`sold-${v.id}`}>
                      <div className="h-2 bg-gradient-to-r from-emerald-400 to-emerald-600" />
                      <div className="p-4 space-y-3">
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="font-bold text-lg text-slate-900">{v.brand} {v.model}</h3>
                            <p className="text-sm text-slate-500">{v.plate_number}</p>
                          </div>
                          <Badge className="bg-emerald-100 text-emerald-700 text-xs">Vendu</Badge>
                        </div>
                        {contract && (
                          <div className="text-sm space-y-1">
                            <div className="flex justify-between">
                              <span className="text-slate-500">Client</span>
                              <span className="font-medium">{contract.client_name}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-500">Prix TTC</span>
                              <span className="font-mono">{formatPrice(contract.price_ttc)}</span>
                            </div>
                            {contract.warranty && contract.warranty.type && (
                              <div className="flex justify-between">
                                <span className="text-slate-500">Garantie</span>
                                <span>{contract.warranty.label}</span>
                              </div>
                            )}
                            {contract.trade_in && contract.trade_in.brand && (
                              <div className="flex justify-between">
                                <span className="text-slate-500">Reprise</span>
                                <span>{contract.trade_in.brand} {contract.trade_in.model}</span>
                              </div>
                            )}
                            <div className="flex justify-between font-bold border-t pt-1">
                              <span>Total</span>
                              <span className="text-emerald-700">{formatPrice(contract.total)}</span>
                            </div>
                            <div className="text-xs text-slate-400">{contract.concluded_at && new Date(contract.concluded_at).toLocaleDateString('fr-CH')}</div>
                          </div>
                        )}
                        <div className="flex gap-2 pt-2 border-t border-slate-100">
                          <Button size="sm" variant="outline" className="flex-1 text-xs" onClick={() => setShowDetail(v)}
                            data-testid={`view-sold-${v.id}`}>
                            <Eye className="w-3.5 h-3.5 mr-1" /> Details
                          </Button>
                          {contract && (
                            <Button size="sm" variant="outline" className="flex-1 text-xs"
                              onClick={async () => {
                                const p = new URLSearchParams();
                                if (contract.client_id) p.set('client_id', contract.client_id);
                                if (contract.warranty) { p.set('warranty_months', contract.warranty.months || 0); p.set('warranty_details', `${contract.warranty.label || ''} - CHF ${contract.warranty_price || 0}`); }
                                p.set('price_ht', contract.price_ht || 0);
                                p.set('price_ttc', contract.price_ttc || 0);
                                if (contract.trade_in) {
                                  if (contract.trade_in.brand) p.set('tradein_brand', contract.trade_in.brand);
                                  if (contract.trade_in.model) p.set('tradein_model', contract.trade_in.model);
                                  if (contract.trade_in.plate_number) p.set('tradein_plate', contract.trade_in.plate_number);
                                  if (contract.trade_in.chassis_number) p.set('tradein_chassis', contract.trade_in.chassis_number);
                                  if (contract.trade_in.color) p.set('tradein_color', contract.trade_in.color);
                                  if (contract.trade_in.first_registration) p.set('tradein_first_reg', contract.trade_in.first_registration);
                                  if (contract.trade_in.mileage) p.set('tradein_km', contract.trade_in.mileage);
                                  if (contract.trade_in.value) p.set('tradein_value', contract.trade_in.value);
                                  if (contract.trade_in.expertise_date) p.set('tradein_expertise', contract.trade_in.expertise_date);
                                }
                                const { downloadPdf } = await import('@/utils/api');
                                await downloadPdf(`${API}/used-vehicles/${v.id}/contrat-vente?${p.toString()}`, `contrat-vente-${v.id}.pdf`).catch(() => toast.error('Erreur telechargement'));
                              }}
                              data-testid={`download-contract-${v.id}`}>
                              <Download className="w-3.5 h-3.5 mr-1" /> Contrat PDF
                            </Button>
                          )}
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
      <SaleProcessDialog vehicle={saleVehicle} open={!!saleVehicle} existingContract={saleContract}
        onClose={() => { setSaleVehicle(null); setSaleContract(null); }}
        onContractSaved={() => { load(); loadSold(); loadPending(); }} />
      <PreparationChecklist
        vehicleId={prepDialog.vehicleId}
        vehicleName={prepDialog.vehicleName}
        type={prepDialog.type}
        open={prepDialog.open}
        onClose={() => setPrepDialog(p => ({ ...p, open: false }))}
        onUpdate={() => { load(); loadPending(); }}
      />
      {publishVehicle && (
        <PublishMarketplaceModal vehicle={publishVehicle} onClose={() => setPublishVehicle(null)} />
      )}
    </AdminLayout>
  );
};

// ═══════ SORTABLE PHOTO ITEM (for drag-and-drop) ═══════
const SortablePhoto = ({ id, url, onDelete, index }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition, zIndex: isDragging ? 50 : 'auto', opacity: isDragging ? 0.5 : 1 };
  return (
    <div ref={setNodeRef} style={style} className={`relative rounded-xl overflow-hidden h-24 bg-slate-100 dark:bg-slate-800 group ${isDragging ? 'ring-2 ring-indigo-400 shadow-lg' : ''}`} data-testid={`sortable-photo-${index}`}>
      <img src={`${API}${url.replace('/api', '')}`} alt="" className="w-full h-full object-cover" />
      {index === 0 && <div className="absolute top-1 left-1 bg-indigo-600 text-white text-[8px] font-bold px-1.5 py-0.5 rounded">COVER</div>}
      <div {...attributes} {...listeners} className="absolute top-1 right-1 p-1 bg-black/40 backdrop-blur-sm rounded cursor-grab active:cursor-grabbing text-white hover:bg-black/60 transition-colors">
        <GripVertical className="w-3.5 h-3.5" />
      </div>
      {onDelete && (
        <button onClick={(e) => { e.stopPropagation(); onDelete(); }} className="absolute bottom-1 right-1 p-1 bg-red-500/80 backdrop-blur-sm rounded text-white opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`delete-photo-${index}`}>
          <Trash2 className="w-3 h-3" />
        </button>
      )}
    </div>
  );
};

// ═══════ PUBLISH TO MARKETPLACE MODAL (3 steps: details → photos → review) ═══════
const PublishMarketplaceModal = ({ vehicle, onClose }) => {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    price_public: vehicle.selling_price || '',
    price_pro: '',
    internal_margin: '',
    description: vehicle.description || '',
    condition: vehicle.condition || 'occasion',
    body_state: vehicle.body_state || '',
    body_cost_indicative: vehicle.body_cost_indicative || '',
    body_details: vehicle.body_details || '',
    mech_state: vehicle.mech_state || '',
    mech_cost_indicative: vehicle.mech_cost_indicative || '',
    mech_details: vehicle.mech_details || '',
  });
  const [listingId, setListingId] = useState(null);
  const [photoItems, setPhotoItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleCreateDraft = async () => {
    if (!form.price_pro) { toast.error('Prix professionnel requis'); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/marketplace/publish-used-vehicle/${vehicle.id}`, {
        ...form,
        price_public: parseFloat(form.price_public) || 0,
        price_pro: parseFloat(form.price_pro) || 0,
        internal_margin: parseFloat(form.internal_margin) || 0,
        body_cost_indicative: parseFloat(form.body_cost_indicative) || 0,
        mech_cost_indicative: parseFloat(form.mech_cost_indicative) || 0,
      });
      setListingId(res.data.id);
      setStep(2);
      toast.success('Brouillon cree. Ajoutez des photos.');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    setSaving(false);
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!listingId) return;
    setUploading(true);
    for (const file of files) {
      if (photoItems.length >= 10) break;
      const fd = new FormData();
      fd.append('file', file);
      try {
        const res = await axios.post(`${API}/marketplace/listings/${listingId}/photos`, fd);
        setPhotoItems(prev => [...prev, { id: res.data.file_id, url: res.data.photo_url }]);
      } catch { toast.error(`Erreur upload ${file.name}`); }
    }
    setUploading(false);
  };

  const handleDeletePhoto = async (fileId) => {
    try {
      await axios.delete(`${API}/marketplace/listings/${listingId}/photos/${fileId}`);
      setPhotoItems(prev => prev.filter(p => p.id !== fileId));
      toast.success('Photo supprimee');
    } catch { toast.error('Erreur suppression'); }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = photoItems.findIndex(p => p.id === active.id);
    const newIndex = photoItems.findIndex(p => p.id === over.id);
    const newOrder = arrayMove(photoItems, oldIndex, newIndex);
    setPhotoItems(newOrder);
    try {
      await axios.put(`${API}/marketplace/listings/${listingId}/photos/reorder`, { photo_ids: newOrder.map(p => p.id) });
    } catch { toast.error('Erreur reordonnancement'); }
  };

  const STEPS = [
    { num: 1, label: 'Details & Prix' },
    { num: 2, label: 'Photos' },
    { num: 3, label: 'Apercu' },
  ];

  const fmt = (v) => `CHF ${(parseFloat(v) || 0).toLocaleString('fr-CH', { minimumFractionDigits: 2 })}`;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="publish-marketplace-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShoppingCart className="w-5 h-5 text-indigo-600" />
            Publier sur la Marketplace
          </DialogTitle>
        </DialogHeader>

        <div className="text-sm text-slate-600 mb-2">
          <span className="font-bold text-slate-900 dark:text-white">{vehicle.brand} {vehicle.model}</span>
          {vehicle.plate_number && <span className="ml-2 text-slate-400">({vehicle.plate_number})</span>}
          {vehicle.mileage > 0 && <span className="ml-2 text-slate-400">{Number(vehicle.mileage).toLocaleString()} km</span>}
        </div>

        {/* Step indicator */}
        <div className="flex items-center gap-2 mb-4" data-testid="publish-steps">
          {STEPS.map((s, i) => (
            <React.Fragment key={s.num}>
              <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${step === s.num ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300' : step > s.num ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' : 'bg-slate-100 text-slate-400 dark:bg-slate-800'}`}>
                {step > s.num ? <Check className="w-3 h-3" /> : <span>{s.num}</span>}
                <span className="hidden sm:inline">{s.label}</span>
              </div>
              {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 rounded ${step > s.num ? 'bg-emerald-300' : 'bg-slate-200 dark:bg-slate-700'}`} />}
            </React.Fragment>
          ))}
        </div>

        {/* STEP 1: Details & Prix */}
        {step === 1 && (
          <div className="space-y-4" data-testid="publish-step-1">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-500">Prix vente public (CHF)</Label>
                <Input type="number" value={form.price_public} onChange={e => set('price_public', e.target.value)} placeholder="15000" data-testid="publish-price-public" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Prix pro inter-garage (CHF) *</Label>
                <Input type="number" value={form.price_pro} onChange={e => set('price_pro', e.target.value)} placeholder="12000" data-testid="publish-price-pro" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-500">Marge interne (CHF)</Label>
                <Input type="number" value={form.internal_margin} onChange={e => set('internal_margin', e.target.value)} placeholder="2000" data-testid="publish-margin" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Etat</Label>
                <Select value={form.condition} onValueChange={v => set('condition', v)}>
                  <SelectTrigger className="h-9" data-testid="publish-condition"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="neuf">Neuf</SelectItem>
                    <SelectItem value="occasion">Occasion</SelectItem>
                    <SelectItem value="a_reviser">A reviser</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Description</Label>
              <Textarea value={form.description} onChange={e => set('description', e.target.value)} placeholder="Bon etat, revision complete..." className="min-h-[60px]" data-testid="publish-description" />
            </div>

            {/* Refurbishment costs */}
            <div className="border-t pt-3 dark:border-slate-700">
              <h4 className="text-xs font-bold text-slate-500 uppercase mb-3">Frais indicatifs de remise en etat</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Carrosserie */}
                <div className="space-y-2 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-xl">
                  <Label className="text-xs font-semibold text-slate-700 dark:text-slate-300">Carrosserie</Label>
                  <Select value={form.body_state} onValueChange={v => set('body_state', v)}>
                    <SelectTrigger className="h-8 text-xs" data-testid="publish-body-state"><SelectValue placeholder="Etat..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bon">Bon etat</SelectItem>
                      <SelectItem value="moyen">Moyen</SelectItem>
                      <SelectItem value="a_faire">A faire</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input type="number" value={form.body_cost_indicative} onChange={e => set('body_cost_indicative', e.target.value)} placeholder="Cout CHF" className="h-8 text-xs" data-testid="publish-body-cost" />
                  <Input value={form.body_details} onChange={e => set('body_details', e.target.value)} placeholder="Details..." className="h-8 text-xs" data-testid="publish-body-details" />
                </div>
                {/* Mecanique */}
                <div className="space-y-2 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-xl">
                  <Label className="text-xs font-semibold text-slate-700 dark:text-slate-300">Mecanique</Label>
                  <Select value={form.mech_state} onValueChange={v => set('mech_state', v)}>
                    <SelectTrigger className="h-8 text-xs" data-testid="publish-mech-state"><SelectValue placeholder="Etat..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="service_a_jour">Service a jour</SelectItem>
                      <SelectItem value="frais_a_prevoir">Frais a prevoir</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input type="number" value={form.mech_cost_indicative} onChange={e => set('mech_cost_indicative', e.target.value)} placeholder="Cout CHF" className="h-8 text-xs" data-testid="publish-mech-cost" />
                  <Input value={form.mech_details} onChange={e => set('mech_details', e.target.value)} placeholder="Details..." className="h-8 text-xs" data-testid="publish-mech-details" />
                </div>
              </div>
            </div>

            <Button onClick={handleCreateDraft} disabled={saving} className="w-full" data-testid="publish-step1-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {saving ? 'Creation...' : 'Suivant — Ajouter les photos'}
            </Button>
          </div>
        )}

        {/* STEP 2: Photos with drag-and-drop */}
        {step === 2 && (
          <div className="space-y-4" data-testid="publish-step-2">
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-3 text-xs text-amber-700 dark:text-amber-300">
              <Camera className="w-4 h-4 inline mr-1" />
              Minimum <strong>7 photos</strong> obligatoires. Glissez-deposez pour reordonner. La 1ere photo sera la couverture.
            </div>

            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={photoItems.map(p => p.id)} strategy={rectSortingStrategy}>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {photoItems.map((p, i) => (
                    <SortablePhoto key={p.id} id={p.id} url={p.url} index={i} onDelete={() => handleDeletePhoto(p.id)} />
                  ))}
                  {photoItems.length < 10 && (
                    <label className="h-24 rounded-xl border-2 border-dashed border-slate-300 dark:border-slate-600 flex flex-col items-center justify-center cursor-pointer hover:border-indigo-400 hover:bg-indigo-50/30 dark:hover:bg-indigo-900/20 transition-colors" data-testid="upload-marketplace-photo">
                      <input type="file" accept="image/*" multiple onChange={handlePhotoUpload} className="hidden" />
                      {uploading ? (
                        <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
                      ) : (
                        <>
                          <Camera className="w-6 h-6 text-slate-300" />
                          <span className="text-[10px] text-slate-400 mt-1">{photoItems.length}/10</span>
                        </>
                      )}
                    </label>
                  )}
                </div>
              </SortableContext>
            </DndContext>

            <div className="flex items-center justify-between text-xs">
              <span className={`font-medium ${photoItems.length >= 7 ? 'text-emerald-600' : 'text-amber-600'}`}>
                {photoItems.length}/7 photos minimum
              </span>
              {photoItems.length < 7 && <span className="text-slate-400">Encore {7 - photoItems.length} photo(s) requise(s)</span>}
            </div>

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep(1)} className="flex-1" data-testid="publish-back-step1">Retour</Button>
              <Button onClick={() => setStep(3)} disabled={photoItems.length < 7} className="flex-1" data-testid="publish-step2-btn">
                Suivant — Apercu
              </Button>
            </div>
          </div>
        )}

        {/* STEP 3: Review */}
        {step === 3 && (
          <div className="space-y-4" data-testid="publish-step-3">
            <Card className="p-4 rounded-xl space-y-3 bg-slate-50 dark:bg-slate-800/50">
              <h4 className="text-sm font-bold text-slate-900 dark:text-white">Recapitulatif</h4>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                <div><span className="text-slate-400">Vehicule</span><p className="font-medium text-slate-800 dark:text-slate-200">{vehicle.brand} {vehicle.model}</p></div>
                <div><span className="text-slate-400">Etat</span><p className="font-medium text-slate-800 dark:text-slate-200">{form.condition === 'neuf' ? 'Neuf' : form.condition === 'occasion' ? 'Occasion' : 'A reviser'}</p></div>
                <div><span className="text-slate-400">Prix public</span><p className="font-medium text-slate-800 dark:text-slate-200">{fmt(form.price_public)}</p></div>
                <div><span className="text-slate-400">Prix pro</span><p className="font-bold text-indigo-700 dark:text-indigo-400">{fmt(form.price_pro)}</p></div>
                {parseFloat(form.internal_margin) > 0 && <div><span className="text-slate-400">Marge interne</span><p className="font-medium text-emerald-600">{fmt(form.internal_margin)}</p></div>}
                <div><span className="text-slate-400">Photos</span><p className="font-medium text-slate-800 dark:text-slate-200">{photoItems.length} photo(s)</p></div>
              </div>
              {form.description && <div className="text-xs"><span className="text-slate-400">Description</span><p className="text-slate-700 dark:text-slate-300 mt-0.5">{form.description}</p></div>}
              {(form.body_state || form.mech_state) && (
                <div className="border-t pt-2 dark:border-slate-700">
                  <span className="text-[10px] text-slate-400 uppercase font-bold">Frais remise en etat</span>
                  <div className="grid grid-cols-2 gap-2 mt-1 text-xs">
                    {form.body_state && (
                      <div className="bg-white dark:bg-slate-800 rounded-lg p-2">
                        <span className="text-slate-400">Carrosserie:</span>
                        <span className={`ml-1 font-medium ${form.body_state === 'bon' ? 'text-emerald-600' : form.body_state === 'moyen' ? 'text-orange-500' : 'text-red-500'}`}>
                          {form.body_state === 'bon' ? 'Bon' : form.body_state === 'moyen' ? 'Moyen' : 'A faire'}
                        </span>
                        {parseFloat(form.body_cost_indicative) > 0 && <p className="text-red-600 font-bold">{fmt(form.body_cost_indicative)}</p>}
                      </div>
                    )}
                    {form.mech_state && (
                      <div className="bg-white dark:bg-slate-800 rounded-lg p-2">
                        <span className="text-slate-400">Mecanique:</span>
                        <span className={`ml-1 font-medium ${form.mech_state === 'service_a_jour' ? 'text-emerald-600' : 'text-orange-500'}`}>
                          {form.mech_state === 'service_a_jour' ? 'OK' : 'Frais'}
                        </span>
                        {parseFloat(form.mech_cost_indicative) > 0 && <p className="text-red-600 font-bold">{fmt(form.mech_cost_indicative)}</p>}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </Card>

            {/* Photo preview thumbnails */}
            <div className="flex gap-1.5 overflow-x-auto pb-1">
              {photoItems.slice(0, 6).map((p, i) => (
                <div key={p.id} className="w-14 h-14 rounded-lg overflow-hidden shrink-0 bg-slate-100">
                  <img src={`${API}${p.url.replace('/api', '')}`} alt="" className="w-full h-full object-cover" />
                </div>
              ))}
              {photoItems.length > 6 && <div className="w-14 h-14 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-xs text-slate-400 shrink-0">+{photoItems.length - 6}</div>}
            </div>

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep(2)} className="flex-1" data-testid="publish-back-step2">Retour</Button>
              <Button onClick={onClose} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="publish-finish-btn">
                <Check className="w-4 h-4 mr-1" />
                Terminer la publication
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default UsedVehiclesPage;
